import React from 'react';
import { Plane, Users, Wrench, Shield, FileCheck, CheckCircle, ArrowRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from '../components/ui/card';

const AeronauticalServices = () => {
  const { language } = useLanguage();

  const services = [
    {
      icon: Plane,
      title: { fr: 'Achat et Livraison d\'Avions', en: 'Aircraft Purchase and Delivery' },
      description: { fr: 'Accompagnement complet pour l\'acquisition d\'avions civils', en: 'Complete support for civil aircraft acquisition' },
      image: 'https://images.unsplash.com/photo-1569154941061-e231b4725ef1',
      features: [
        { fr: 'Sélection OEM/constructeurs', en: 'OEM/manufacturer selection' },
        { fr: 'Négociation contractuelle', en: 'Contract negotiation' },
        { fr: 'Audits techniques et PPI', en: 'Technical audits and PPI' },
        { fr: 'Démarches import/export', en: 'Import/export procedures' },
        { fr: 'Immatriculation OACI/ICAO', en: 'ICAO registration' }
      ]
    },
    {
      icon: Users,
      title: { fr: 'Formation Équipages', en: 'Crew Training' },
      description: { fr: 'Formation pilotes et techniciens certifiée', en: 'Certified pilot and technician training' },
      image: 'https://images.unsplash.com/photo-1676661096608-cc8535b9e2c0',
      features: [
        { fr: 'Formation pilotes', en: 'Pilot training' },
        { fr: 'Formation techniciens', en: 'Technician training' },
        { fr: 'Conversion de type', en: 'Type conversion' },
        { fr: 'Simulateurs de vol', en: 'Flight simulators' },
        { fr: 'Certifications officielles', en: 'Official certifications' }
      ]
    },
    {
      icon: Wrench,
      title: { fr: 'Maintenance MRO', en: 'MRO Maintenance' },
      description: { fr: 'Maintenance préventive et curative d\'aéronefs', en: 'Preventive and corrective aircraft maintenance' },
      image: 'https://images.unsplash.com/photo-1606904825846-647eb07f5be2',
      features: [
        { fr: 'Maintenance préventive', en: 'Preventive maintenance' },
        { fr: 'Maintenance curative', en: 'Corrective maintenance' },
        { fr: 'Inspections réglementaires', en: 'Regulatory inspections' },
        { fr: 'Pièces détachées d\'origine', en: 'Original spare parts' },
        { fr: 'AOG support 24/7', en: '24/7 AOG support' }
      ]
    },
    {
      icon: Shield,
      title: { fr: 'Assurance et Acceptation', en: 'Insurance and Acceptance' },
      description: { fr: 'Couvertures casco et RC, essais et réception', en: 'Hull and liability coverage, testing and acceptance' },
      image: 'https://images.unsplash.com/photo-1563986768494-4dee2763ff3f',
      features: [
        { fr: 'Couvertures d\'assurance', en: 'Insurance coverage' },
        { fr: 'Vols de réception', en: 'Acceptance flights' },
        { fr: 'Tests techniques', en: 'Technical tests' },
        { fr: 'Procès-verbaux d\'acceptation', en: 'Acceptance reports' },
        { fr: 'Garanties constructeur', en: 'Manufacturer warranties' }
      ]
    },
    {
      icon: FileCheck,
      title: { fr: 'Documentation Technique', en: 'Technical Documentation' },
      description: { fr: 'Fourniture complète de documentation aéronautique', en: 'Complete aeronautical documentation supply' },
      image: 'https://images.unsplash.com/photo-1544197150-b99a580bb7a8',
      features: [
        { fr: 'Manuels de vol', en: 'Flight manuals' },
        { fr: 'Manuels de maintenance', en: 'Maintenance manuals' },
        { fr: 'Documentation technique', en: 'Technical documentation' },
        { fr: 'Historiques de maintenance', en: 'Maintenance records' },
        { fr: 'Certifications et licences', en: 'Certifications and licenses' }
      ]
    }
  ];

  return (
    <div className="min-h-screen">
      <section className="relative py-32 overflow-hidden" style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1569154941061-e231b4725ef1)', backgroundSize: 'cover', backgroundPosition: 'center' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-slate-900/95 to-blue-900/90"></div>
        <div className="relative z-10 container mx-auto px-4 text-center">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-blue-600 rounded-full mb-6">
            <Plane className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
            {language === 'fr' ? 'Services Aéronautiques' : 'Aeronautical Services'}
          </h1>
          <p className="text-xl text-gray-200 max-w-3xl mx-auto">
            {language === 'fr' ? 'Acquisition, formation et maintenance d\'avions civils' : 'Acquisition, training and maintenance of civil aircraft'}
          </p>
        </div>
      </section>

      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <p className="text-lg text-gray-700 leading-relaxed">
              {language === 'fr'
                ? 'PRESTACO accompagne les institutions dans l\'acquisition d\'avions civils adaptés aux missions de transport administratif, liaison ou surveillance non armée. Nous offrons un service complet incluant la sélection, l\'achat, la livraison, la formation et la maintenance.'
                : 'PRESTACO supports institutions in acquiring civil aircraft adapted for administrative transport, liaison or unarmed surveillance missions. We offer a complete service including selection, purchase, delivery, training and maintenance.'}
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {language === 'fr' ? 'Nos Services Aéronautiques' : 'Our Aeronautical Services'}
            </h2>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <Card key={index} className="overflow-hidden hover:shadow-2xl transition-all duration-300 bg-white">
                  <div className="relative h-64 overflow-hidden">
                    <img src={service.image} alt={service.title[language]} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                    <div className="absolute inset-0 bg-gradient-to-t from-slate-900/90 to-transparent"></div>
                    <div className="absolute bottom-4 left-4">
                      <div className="w-14 h-14 bg-white/90 backdrop-blur-sm rounded-lg flex items-center justify-center">
                        <Icon className="h-7 w-7 text-blue-600" />
                      </div>
                    </div>
                  </div>

                  <div className="p-8">
                    <h3 className="text-2xl font-bold text-gray-900 mb-3">{service.title[language]}</h3>
                    <p className="text-gray-600 mb-6">{service.description[language]}</p>
                    <ul className="space-y-3">
                      {service.features.map((feature, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-700">
                          <CheckCircle className="h-5 w-5 text-green-600 mr-3 mt-0.5 flex-shrink-0" />
                          <span>{feature[language]}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              {language === 'fr' ? 'Missions Adaptées' : 'Adapted Missions'}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-slate-50">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {language === 'fr' ? 'Transport' : 'Transport'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'fr' ? 'Transport administratif et VIP' : 'Administrative and VIP transport'}
                </p>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-slate-50 to-blue-50">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {language === 'fr' ? 'Liaison' : 'Liaison'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'fr' ? 'Missions de liaison rapide' : 'Rapid liaison missions'}
                </p>
              </Card>
              <Card className="p-6 bg-gradient-to-br from-blue-50 to-slate-50">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {language === 'fr' ? 'Surveillance' : 'Surveillance'}
                </h3>
                <p className="text-gray-600 text-sm">
                  {language === 'fr' ? 'Surveillance non armée' : 'Unarmed surveillance'}
                </p>
              </Card>
            </div>
          </div>
        </div>
      </section>

      <section className="py-20 bg-blue-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            {language === 'fr' ? 'Projet d\'Acquisition d\'Avions ?' : 'Aircraft Acquisition Project?'}
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            {language === 'fr' ? 'Nos experts aéronautiques vous accompagnent dans votre projet' : 'Our aeronautical experts support you in your project'}
          </p>
          <a href="/contact">
            <button className="bg-white text-blue-600 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-colors text-lg inline-flex items-center">
              {language === 'fr' ? 'Nous Contacter' : 'Contact Us'}
              <ArrowRight className="ml-2 h-5 w-5" />
            </button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default AeronauticalServices;
